import { useEffect, useState } from "react";
import { Link as LinkIcon, Check, Share2 } from "lucide-react";
import { getShareChannels, pushShareEvent } from "@/components/ShareButtons";

// Barra flotante de compartir para posts del blog.
// Desktop: columna vertical fija a la izquierda. Móvil: barra inferior.
// Aparece al empezar a leer (scroll > 500px) para no competir con el hero.

export default function StickyShareBar({ url, title }) {
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const channels = getShareChannels(url, title).filter((c) => c.key !== "email");

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 500);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const open = (channel) => (e) => {
    e.preventDefault();
    pushShareEvent(channel.key, url);
    window.open(channel.href, "_blank", "noopener,noreferrer,width=600,height=520");
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      pushShareEvent("copy_link", url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  return (
    <>
      {/* Desktop — columna vertical */}
      <aside
        data-testid="sticky-share-desktop"
        aria-label="Compartir este artículo"
        className={`hidden xl:flex fixed left-6 top-1/2 -translate-y-1/2 z-40 flex-col items-center gap-2 rounded-full border border-brand-line bg-white/85 backdrop-blur-xl px-2 py-3 shadow-[0_12px_32px_-18px_rgba(27,58,107,0.35)] transition-all duration-500 ${
          visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-6 pointer-events-none"
        }`}
      >
        <Share2 className="h-4 w-4 text-brand-muted mb-1" />
        {channels.map((c) => (
          <a
            key={c.key}
            href={c.href}
            onClick={open(c)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Compartir en ${c.label}`}
            data-testid={`sticky-share-${c.key}`}
            className={`${c.color} h-10 w-10 rounded-full flex items-center justify-center text-white transition-transform duration-200 hover:scale-110`}
          >
            <c.Icon className="h-4 w-4" />
          </a>
        ))}
        <button
          type="button"
          onClick={copy}
          aria-label="Copiar enlace"
          data-testid="sticky-share-copy"
          className="h-10 w-10 rounded-full flex items-center justify-center border border-brand-line text-brand-primary bg-white transition-transform duration-200 hover:scale-110"
        >
          {copied ? <Check className="h-4 w-4 text-green-600" /> : <LinkIcon className="h-4 w-4" />}
        </button>
      </aside>

      {/* Móvil / tablet — barra inferior */}
      <div
        data-testid="sticky-share-mobile"
        aria-label="Compartir este artículo"
        className={`xl:hidden fixed bottom-24 left-3 right-3 z-40 rounded-full border border-brand-line bg-white/92 backdrop-blur-xl shadow-[0_12px_32px_-14px_rgba(27,58,107,0.4)] transition-all duration-300 ${
          visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6 pointer-events-none"
        }`}
      >
        <div className="flex items-center gap-2 px-3 py-2">
          <span className="text-xs text-brand-muted font-outfit font-medium shrink-0 hidden sm:inline">
            Compartir
          </span>
          <div className="flex items-center gap-2 flex-1 justify-evenly">
            {channels.map((c) => (
              <a
                key={c.key}
                href={c.href}
                onClick={open(c)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Compartir en ${c.label}`}
                data-testid={`sticky-share-mobile-${c.key}`}
                className={`${c.color} h-10 w-10 rounded-full flex items-center justify-center text-white active:scale-95 transition-transform`}
              >
                <c.Icon className="h-4 w-4" />
              </a>
            ))}
            <button
              type="button"
              onClick={copy}
              aria-label="Copiar enlace"
              data-testid="sticky-share-mobile-copy"
              className="h-10 w-10 rounded-full flex items-center justify-center border border-brand-line text-brand-primary bg-white active:scale-95 transition-transform"
            >
              {copied ? <Check className="h-4 w-4 text-green-600" /> : <LinkIcon className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
